import { useState, useEffect, useMemo, useRef } from 'react';
import { format } from 'date-fns';
import { History, Search, ArrowRight, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { ADMIN_COLORS } from '../constants/bookingConst';
import { useConfig } from '../context/ConfigContext';
import { getCourtLabel, formatTimeSlotRange, formatSlotRanges } from '../utils/bookingUtils';
import { formatMoney } from '../utils/currencyUtil';
import {
  AUDIT_ACTION_TYPES,
  AUDIT_ACTION_LABELS,
  ENTITY_TYPES,
  STATUS_CHANGE_ACTIONS,
  subscribeAuditLogInRange,
  fetchBookingsByIds,
  fetchOpenPlaySessionsByIds,
  buildSlotsByCourt,
  GUEST_ACTOR,
  PLACEHOLDER_ADMIN_ACTOR,
  DEFAULT_AUDIT_DAYS,
} from '../utils/auditLogUtil';
import '../css/AdminAuditTrail.css';

// Same jassen light palette every other admin screen reads off the
// Config -> Theme colors (constants/bookingConst.js) — this is what makes
// this screen "follow the color template from config" for free, the same
// way AdminBooking/AdminBookingsList already do.
const COLORS = ADMIN_COLORS;

// Semantic tint per action, independent of the club's brand color — a
// decline should always read as "warning-ish" whatever theme is configured,
// the same reasoning ADMIN_COLORS.success/warning/danger already applies to
// booking status badges elsewhere in the admin.
const ACTION_TONE = {
  [AUDIT_ACTION_TYPES.BOOKING_SUBMITTED]: 'info',
  [AUDIT_ACTION_TYPES.BOOKING_CONFIRMED]: 'success',
  [AUDIT_ACTION_TYPES.BOOKING_DECLINED]: 'danger',
  [AUDIT_ACTION_TYPES.BOOKING_CANCELLED]: 'danger',
  [AUDIT_ACTION_TYPES.BOOKING_STATUS_CHANGED]: 'warning',
  // Confirmed, but with money still outstanding — the one booking outcome
  // that is neither settled nor refused, and the reason this screen gets
  // opened when the day's takings don't reconcile.
  [AUDIT_ACTION_TYPES.BOOKING_PARTIALLY_PAID]: 'warning',
  [AUDIT_ACTION_TYPES.BOOKING_FULLY_PAID]: 'success',
  // Records deleted in bulk and unrecoverably — the loudest thing an admin
  // can do to this data, and the only surviving trace of the records.
  [AUDIT_ACTION_TYPES.BOOKINGS_CLEANED_UP]: 'danger',
  [AUDIT_ACTION_TYPES.OPENPLAY_SESSION_CREATED]: 'info',
  [AUDIT_ACTION_TYPES.OPENPLAY_SESSION_EDITED]: 'warning',
  // The loudest of the Open Play set: it takes the session off every screen
  // and cancels everyone on it in one go.
  [AUDIT_ACTION_TYPES.OPENPLAY_SESSION_CANCELLED]: 'danger',
  [AUDIT_ACTION_TYPES.OPENPLAY_PLAYER_JOINED]: 'info',
  [AUDIT_ACTION_TYPES.OPENPLAY_PLAYER_CONFIRMED]: 'success',
  [AUDIT_ACTION_TYPES.OPENPLAY_PLAYER_DECLINED]: 'danger',
  [AUDIT_ACTION_TYPES.OPENPLAY_PLAYER_REMOVED]: 'danger',
  // A promo changes prices, so creating or editing one is a warning-weight
  // event rather than routine news; deleting or switching one off is the
  // loudest of the set because prices revert with no further notice.
  [AUDIT_ACTION_TYPES.PROMO_CREATED]: 'info',
  [AUDIT_ACTION_TYPES.PROMO_UPDATED]: 'warning',
  [AUDIT_ACTION_TYPES.PROMO_DELETED]: 'danger',
  [AUDIT_ACTION_TYPES.PROMO_ENABLED]: 'success',
  [AUDIT_ACTION_TYPES.PROMO_DISABLED]: 'danger',
  // Closing an hour takes it off the guest grid, so it reads as a warning
  // rather than a failure; putting it back is ordinary good news.
  [AUDIT_ACTION_TYPES.SLOT_CLOSED]: 'warning',
  [AUDIT_ACTION_TYPES.SLOT_REOPENED]: 'success',
  [AUDIT_ACTION_TYPES.SLOT_STATUS_CHANGED]: 'info',
  // Changes what guests may book, so it carries the same weight as a promo edit.
  [AUDIT_ACTION_TYPES.BOOKING_SETTINGS_UPDATED]: 'warning',
};

const TONE_COLORS = {
  info: COLORS.cyanDeep,
  success: COLORS.success,
  warning: COLORS.warning,
  danger: COLORS.danger,
};

// Tinted backgrounds for the action pill. NOT derived by string-appending an
// alpha suffix onto TONE_COLORS — COLORS.cyanDeep is a CSS var() reference
// ("var(--jt-cyan-deep, #5b948c)"), and `${var(...)}1a` is invalid CSS, so
// that tint would silently fail to render. `info` uses the theme's own
// pre-blended soft tint instead; success/warning/danger are already plain
// hex literals in ADMIN_COLORS (deliberately not theme-tied — see its
// comment), so the alpha suffix is safe there.
const TONE_BG = {
  info: COLORS.cyanSoft,
  success: `${COLORS.success}1a`,
  warning: `${COLORS.warning}1a`,
  danger: `${COLORS.danger}1a`,
};

const STATUS_LABELS = {
  awaiting_payment: 'Awaiting payment',
  pending: 'Pending',
  confirmed: 'Confirmed',
  approved: 'Approved',
  disapproved: 'Declined',
  cancelled: 'Cancelled',
  pending_payment: 'Awaiting payment',
  declined: 'Declined',
  // An admin took an already-confirmed player off a hosted session, as
  // opposed to rejecting a join request that was never approved.
  removed: 'Removed',
  active: 'Active',
  deleted: 'Deleted',
  // The "Manage availability" GRID statuses, which the slot_closed /
  // slot_reopened rows carry in the same Old -> New column. Without these
  // the column fell back to the raw lowercase value ("open -> closed").
  open: 'Open',
  closed: 'Closed',
  booked: 'Booked',
  processing: 'Processing',
  passed: 'Passed',
};
const formatStatus = (status) => (status ? (STATUS_LABELS[status] || status) : '—');

/** "Aug 24, 2026, 3:41 PM" from an entry's numeric `timestamp`. */
const formatEntryDate = (ts) => (ts ? format(new Date(ts), 'MMM d, yyyy, h:mm a') : '—');

/**
 * The Details cell, in one shape for EVERY action — booking or Open Play,
 * guest-performed or admin-performed:
 *
 *   Guest Name: asd                        (only when someone else acted)
 *   2026-08-24
 *   Court 2: 11:00PM-12:00MN
 *   Pickleball · 2 players                 (Open Play only)
 *
 * Open Play used to render as one inline "a · b · c" string while bookings
 * got stacked lines, so the same column read two different ways depending on
 * the row. Sessions carry the same `slotsByCourt` map a booking does, so
 * their courts and hours now come out through the identical code path; a
 * join has no court of its own and contributes its hour and player count to
 * the trailing meta line instead.
 *
 * Court names come from Config -> Sports & Courts via getCourtLabel and
 * hours through formatTimeSlotRange, matching how slots are written
 * everywhere else in the admin rather than the raw "10-11PM" enum.
 */
/**
 * The money line on a payment entry ("Paid ₱600 · Balance ₱600 · Total
 * ₱1,200"), or null when this entry recorded no amounts.
 *
 * Reads `entry.changes`, which every call site fills but nothing used to
 * render — so a partial payment logged the figures and then showed an admin
 * nothing but the action's name. Written defensively: the three fields are
 * independent, an older entry may carry none of them, and a zero balance is
 * a real value worth printing (it is what "Balance settled" means).
 *
 * @param {object|null} changes - entry.changes.
 * @param {object} config - the club config, for the currency symbol.
 * @returns {string|null}
 */
const formatPaymentChanges = (changes, config) => {
  if (!changes) return null;
  const parts = [];
  if (Number.isFinite(Number(changes.amountPaid))) {
    parts.push(`Paid ${formatMoney(Number(changes.amountPaid), config)}`);
  }
  if (Number.isFinite(Number(changes.balanceDue))) {
    parts.push(`Balance ${formatMoney(Number(changes.balanceDue), config)}`);
  }
  if (Number.isFinite(Number(changes.total))) {
    parts.push(`Total ${formatMoney(Number(changes.total), config)}`);
  }
  return parts.length > 0 ? parts.join(' · ') : null;
};

function AuditDetails({ entry, config, booking, session, actorLabel }) {
  const s = entry.summary || {};
  // Shown above the meta line on the entries that carry amounts (partial
  // payment, balance settled); null on every other action, which is all of
  // the ones that have ever been logged before now.
  const paymentLine = formatPaymentChanges(entry.changes, config);

  // A promo has no courts, hours or guest to render — everything below this
  // point is about bookings — so it gets its own compact line and returns
  // before any of that machinery runs.
  if (s.type === 'promo') {
    const run = [s.startDate, s.endDate].filter(Boolean).join(' – ');
    const meta = [
      s.sportName || 'All sports',
      run || 'Any date',
      `${s.rateCount || 0} rate${s.rateCount === 1 ? '' : 's'}`,
    ];
    return (
      <div className="aat-detail-lines">
        <span className="aat-detail-guest">{s.promoName || 'Untitled promo'}</span>
        <span className="aat-detail-meta">{meta.join(' · ')}</span>
      </div>
    );
  }

  // A Time Slot Button Colors save (Config -> Theme) — no booking or guest,
  // just which slot states changed colour, or that they were all reset.
  if (s.type === 'slotColors') {
    return (
      <div className="aat-detail-lines">
        <span className="aat-detail-meta">
          {s.reset ? 'All slot colors reset to default' : `Changed: ${s.statuses || '—'}`}
        </span>
      </div>
    );
  }

  // A UI Design change (Config -> Theme) — just which version it moved between.
  if (s.type === 'uiDesign') {
    return (
      <div className="aat-detail-lines">
        <span className="aat-detail-meta">{`${s.from || '—'} \u2192 ${s.to || '—'}`}</span>
      </div>
    );
  }

  // An on/off System setting (e.g. "Ask reason for closing timeslot") —
  // which setting, and which way it was switched.
  if (s.type === 'settingToggle') {
    return (
      <div className="aat-detail-lines">
        <span className="aat-detail-meta">{`${s.setting || 'Setting'}: ${s.from || '—'} \u2192 ${s.to || '—'}`}</span>
      </div>
    );
  }

  // A Booking Settings save (Config -> Booking Settings) — the rule changes
  // as one line, e.g. "Minimum hours 1 → 2 · Block bookings 0 → 1".
  if (s.type === 'bookingSettings') {
    return (
      <div className="aat-detail-lines">
        <span className="aat-detail-meta">{s.changes || '—'}</span>
      </div>
    );
  }

  // An availability image has no booking or guest either — just the day it
  // showed, which template, and how open the day was when it was made.
  if (s.type === 'availabilityImage') {
    const meta = [
      s.sportName,
      s.templateLabel || s.templateId,
      `${s.openSlots ?? 0} of ${s.totalSlots ?? 0} slots open`,
    ].filter(Boolean);
    return (
      <div className="aat-detail-lines">
        {s.date && <span className="aat-detail-date">{s.date}</span>}
        <span className="aat-detail-meta">{meta.join(' \u00b7 ')}</span>
      </div>
    );
  }

  // A guest saving a payment QR at checkout: which QR, for which reservation.
  if (s.type === 'paymentQr') {
    const meta = [
      s.qrName ? `QR: ${s.qrName}` : 'Payment QR',
      s.confirmationId ? `Ref ${s.confirmationId}` : null,
    ].filter(Boolean);
    return (
      <div className="aat-detail-lines">
        {s.date && <span className="aat-detail-date">{s.date}</span>}
        <span className="aat-detail-meta">{meta.join(' · ')}</span>
      </div>
    );
  }

  // Preference order: the entry's own snapshot, then the Open Play session
  // it belongs to (a join stores no courts of its own — only which session
  // it joined), then the booking record. All three are the same
  // { [court]: [timeSlot, ...] } shape, so one renderer covers every case.
  let slotsByCourt = {};
  if (s.slotsByCourt && Object.keys(s.slotsByCourt).length > 0) {
    slotsByCourt = s.slotsByCourt;
  } else if (session?.courts && Object.keys(session.courts).length > 0) {
    slotsByCourt = session.courts;
  } else if (booking) {
    slotsByCourt = buildSlotsByCourt([{ timeSlots: booking.timeSlots }]);
  }
  const date = s.date || booking?.date || session?.date || null;
  const packageName = s.packageName || booking?.packageName || null;

  // The guest is named only when SOMEONE ELSE took the action. On a guest's
  // own submission or join the Performed By column already says who they
  // are, and repeating it here is noise; on an admin confirm/decline/cancel
  // the row would otherwise never say whose booking was acted on. Derived by
  // comparing the actor to the guest rather than by branching on action
  // type, so an admin-entered walk-in also names its guest correctly.
  const guestName = s.guestName || booking?.userName || null;
  const showGuestName = Boolean(guestName) && actorLabel !== guestName;

  const courts = Object.keys(slotsByCourt).sort((a, b) => Number(a) - Number(b));

  // Everyone named on the join. The booker is already in Performed By, so
  // this is the rest of the party — the thing an admin actually needs when
  // checking who turned up against who paid.
  const players = Array.isArray(s.players) ? s.players.filter(Boolean) : [];

  // Facts with no court to hang them on — sport, hour, party size, cap.
  // One trailing line so they never displace the court lines.
  const meta = [];
  if (s.sportName || session?.sportName) meta.push(s.sportName || session.sportName);
  if (s.timeSlot) meta.push(formatTimeSlotRange(s.timeSlot));
  if (s.numPlayers) meta.push(`${s.numPlayers} player${s.numPlayers === 1 ? '' : 's'}`);
  if (s.maxPlayers || session?.maxPlayers) meta.push(`max ${s.maxPlayers || session.maxPlayers}`);
  if (s.guestPartialPercent) meta.push(`Partial payment chosen (${s.guestPartialPercent}%)`);

  if (!showGuestName && !date && courts.length === 0 && meta.length === 0 && players.length === 0 && !paymentLine) {
    return <span style={{ color: COLORS.inkFaint }}>—</span>;
  }

  return (
    <div className="aat-detail-lines">
      {showGuestName && (
        <span className="aat-detail-guest">Guest Name: {guestName}</span>
      )}
      {/* A court and an hour with no day is ambiguous the moment the range
          covers more than one — kept small, above the court lines. */}
      {date && (
        <span className="aat-detail-date">
          {date}{s.dates && s.dates.length > 1 ? ` (+${s.dates.length - 1} more)` : ''}
          {packageName ? ` · ${packageName}` : ''}
        </span>
      )}
      {courts.map((court) => (
        <span key={court} className="aat-detail-court">
          <strong>{getCourtLabel(config, court)}:</strong>{' '}
          {formatSlotRanges(slotsByCourt[court] || [])}
        </span>
      ))}
      {players.length > 0 && (
        <span className="aat-detail-players">
          <strong>Players:</strong> {players.join(', ')}
        </span>
      )}
      {/* What was taken and what is left on it — the whole point of logging
          a payment, and the figure someone reconciling the day's takings is
          reading this row for. */}
      {paymentLine && (
        <span className="aat-detail-payment">{paymentLine}</span>
      )}
      {meta.length > 0 && (
        <span className="aat-detail-meta">{meta.join(' · ')}</span>
      )}
    </div>
  );
}

/**
 * Who to show in Performed By.
 *
 * `performedBy` is authoritative when it names someone. It is only
 * substituted when it holds one of the two generic placeholders — an entry
 * written before the guest's name was recorded, or by a tab running an older
 * bundle — in which case the booking's own `userName` is the person who took
 * the action and is a strictly better answer than the word "guest".
 */
const resolveActorLabel = (entry, booking) => {
  const recorded = entry.performedBy;
  const isPlaceholder = !recorded || recorded === GUEST_ACTOR || recorded === PLACEHOLDER_ADMIN_ACTOR;
  if (!isPlaceholder) return recorded;
  // Only a GUEST placeholder may be replaced by the guest's name — an admin
  // placeholder means the acting admin genuinely wasn't recorded, and naming
  // the guest there would credit the wrong person entirely.
  if (recorded !== PLACEHOLDER_ADMIN_ACTOR) {
    const name = entry.summary?.guestName || booking?.userName;
    if (name) return name;
  }
  return recorded || '—';
};

const ACTION_FILTER_OPTIONS = [
  { value: 'all', label: 'All actions' },
  ...Object.entries(AUDIT_ACTION_LABELS).map(([value, label]) => ({ value, label })),
];

const ENTITY_FILTER_OPTIONS = [
  { value: 'all', label: 'All entities' },
  { value: ENTITY_TYPES.BOOKING, label: 'Bookings' },
  { value: ENTITY_TYPES.OPENPLAY_SESSION, label: 'Open Play sessions' },
  { value: ENTITY_TYPES.OPENPLAY_JOIN, label: 'Open Play joins' },
  { value: ENTITY_TYPES.SLOT, label: 'Time slots' },
  // Written by the Redeploy client workflow rather than by the app, so these
  // rows exist even on a day nobody touched a booking.
  { value: ENTITY_TYPES.DEPLOYMENT, label: 'Deployments' },
  // Settings saved from Config (currently the time slot button colours).
  { value: ENTITY_TYPES.CONFIG, label: 'Configuration' },
];

/** "yyyy-MM-dd" for a date `daysAgo` days before today, for the date inputs. */
const isoDaysAgo = (daysAgo) => {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  return format(d, 'yyyy-MM-dd');
};

function AdminAuditTrail() {
  // Court display names come from Config -> Sports & Courts.
  const { config } = useConfig();
  // One live listener, bounded by the selected date range — see
  // subscribeAuditLogInRange. There is no second unbounded "recent" query.
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  // A failed read and an empty log must not look the same on this screen —
  // see subscribeAuditLogInRange's onError note.
  const [loadError, setLoadError] = useState(null);
  // Booking records behind the rows currently on screen, keyed by booking id.
  // Only consulted where an entry's own snapshot is missing a field — see
  // fetchBookingsByIds. `null` means "looked up, not found", so a bad id is
  // not retried forever.
  const [bookingCache, setBookingCache] = useState({});
  // Ids already requested — see the effect below for why this is a ref and
  // not part of `bookingCache`.
  const requestedBookingIds = useRef(new Set());
  // Open Play sessions behind the rows on screen, same mechanism as the
  // booking cache above.
  const [sessionCache, setSessionCache] = useState({});
  const requestedSessionIds = useRef(new Set());

  // First load covers the last DEFAULT_AUDIT_DAYS days only, so opening the
  // screen never pulls the whole log. Widening the range is an explicit act:
  // changing either date re-subscribes with the new bounds and nothing more
  // is fetched until then.
  const [dateFilter, setDateFilter] = useState({
    from: isoDaysAgo(DEFAULT_AUDIT_DAYS - 1),
    to: isoDaysAgo(0),
  });
  const [actionFilter, setActionFilter] = useState('all');
  const [entityFilter, setEntityFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 12;

  const isRanged = Boolean(dateFilter.from && dateFilter.to);

  // Re-subscribes whenever the range changes — this is the only fetch on the
  // screen, and it is live, so an action taken while the screen is open lands
  // in the table immediately without a refetch.
  useEffect(() => {
    if (!isRanged) { setEntries([]); setLoading(false); return undefined; }
    setLoading(true);
    const start = new Date(`${dateFilter.from}T00:00:00`).getTime();
    const end = new Date(`${dateFilter.to}T23:59:59.999`).getTime();
    const unsubscribe = subscribeAuditLogInRange(
      start,
      end,
      (list) => { setLoadError(null); setEntries(list); setLoading(false); },
      (err) => { setLoadError(err?.message || String(err)); setLoading(false); },
    );
    return unsubscribe;
  }, [isRanged, dateFilter.from, dateFilter.to]);

  const baseEntries = entries;

  const filteredEntries = useMemo(() => {
    let list = baseEntries;
    if (actionFilter !== 'all') list = list.filter(e => e.actionType === actionFilter);
    if (entityFilter !== 'all') list = list.filter(e => e.entityType === entityFilter);
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter((e) => {
        if (e.entityId?.toLowerCase().includes(q)) return true;
        if (e.performedBy?.toLowerCase().includes(q)) return true;
        if (e.summary?.guestName?.toLowerCase().includes(q)) return true;
        if (e.summary?.confirmationId?.toLowerCase().includes(q)) return true;
        return false;
      });
    }
    return list;
  }, [baseEntries, actionFilter, entityFilter, searchQuery]);

  useEffect(() => { setCurrentPage(1); }, [actionFilter, entityFilter, searchQuery, isRanged]);

  const totalPages = Math.max(1, Math.ceil(filteredEntries.length / ITEMS_PER_PAGE));
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const pageEntries = filteredEntries.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  // Back-fills the booking behind any row whose own snapshot is thin — an
  // entry written before a field existed, or by a tab running an older
  // bundle. Scoped to the visible page and cached, so it is at most a dozen
  // direct key reads and each id is fetched once per session.
  // "Already asked for" is tracked in a REF, not in the cache state, and the
  // effect deliberately does NOT depend on `bookingCache`.
  //
  // The previous version did both of those the other way round and defeated
  // itself: it seeded the cache with null placeholders to avoid duplicate
  // requests, which changed `bookingCache`, which was a dependency, so React
  // ran the effect's cleanup before re-running it — flipping `cancelled` on
  // the fetch that was still in flight. The result then hit `if (!cancelled)`
  // and was discarded, leaving every id parked at null forever and Details
  // with nothing to fall back to. A ref is the right home for this because
  // de-duplication is bookkeeping, not rendered state.
  useEffect(() => {
    // Every booking row on the page, unconditionally — not only the ones
    // whose snapshot looks thin. A partially-populated snapshot (a guest
    // name but no slots, say) would otherwise slip through the test and
    // render an empty Details cell with no way to recover.
    const needed = pageEntries
      .filter(e => e.entityType === ENTITY_TYPES.BOOKING && e.entityId)
      .map(e => e.entityId)
      .filter(id => !requestedBookingIds.current.has(id));
    if (needed.length === 0) return;
    needed.forEach(id => requestedBookingIds.current.add(id));
    fetchBookingsByIds(needed).then((map) => {
      setBookingCache(prev => ({ ...prev, ...map }));
    });
  }, [pageEntries]);

  // Open Play sessions for the rows on screen. A SESSION entry's own id is
  // the session; a JOIN entry names its session in the summary. Both are
  // resolved so either kind of row can show the courts and hours the session
  // covers — a join record holds none of its own.
  useEffect(() => {
    const needed = pageEntries
      .map((e) => {
        if (e.entityType === ENTITY_TYPES.OPENPLAY_SESSION) return e.entityId;
        if (e.entityType === ENTITY_TYPES.OPENPLAY_JOIN) return e.summary?.sessionId;
        return null;
      })
      .filter(id => id && !requestedSessionIds.current.has(id));
    if (needed.length === 0) return;
    needed.forEach(id => requestedSessionIds.current.add(id));
    fetchOpenPlaySessionsByIds(needed).then((map) => {
      setSessionCache(prev => ({ ...prev, ...map }));
    });
  }, [pageEntries]);

  // ---- Stat cards — computed off the same filtered set the table shows,
  // so the numbers above always match what's listed below. ----
  const stats = useMemo(() => {
    const counts = {};
    filteredEntries.forEach((e) => { counts[e.actionType] = (counts[e.actionType] || 0) + 1; });
    const bookingSubmitted = counts[AUDIT_ACTION_TYPES.BOOKING_SUBMITTED] || 0;
    const bookingConfirmed = counts[AUDIT_ACTION_TYPES.BOOKING_CONFIRMED] || 0;
    const bookingDeclined = counts[AUDIT_ACTION_TYPES.BOOKING_DECLINED] || 0;
    const bookingCancelled = counts[AUDIT_ACTION_TYPES.BOOKING_CANCELLED] || 0;
    const openPlayActions = Object.entries(counts)
      .filter(([type]) => type.startsWith('openplay_'))
      .reduce((sum, [, n]) => sum + n, 0);
    return { total: filteredEntries.length, bookingSubmitted, bookingConfirmed, bookingDeclined, bookingCancelled, openPlayActions, counts };
  }, [filteredEntries]);

  const breakdownRows = useMemo(() => {
    const max = Math.max(1, ...Object.values(stats.counts));
    return Object.entries(AUDIT_ACTION_LABELS)
      .map(([type, label]) => ({ type, label, count: stats.counts[type] || 0 }))
      .filter(row => row.count > 0)
      .sort((a, b) => b.count - a.count)
      .map(row => ({ ...row, pct: Math.round((row.count / max) * 100) }));
  }, [stats.counts]);

  // Resets to the default window rather than clearing the range outright —
  // with no bounds there is nothing to subscribe to, and "show me everything"
  // is exactly the unbounded read this screen is meant to avoid.
  const clearDateFilter = () => setDateFilter({
    from: isoDaysAgo(DEFAULT_AUDIT_DAYS - 1),
    to: isoDaysAgo(0),
  });

  return (
    <div className="admin-audit-trail">
      <div className="aat-header">
        <h2 className="section-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <History size={20} color={COLORS.cyanDeep} /> Audit Trail
          <span className="aat-count-badge">{filteredEntries.length}</span>
        </h2>
      </div>

      {/* ---- Stat cards ---- */}
      <div className="aat-stat-grid">
        <div className="aat-card">
          <div className="aat-card-title">Booking Actions</div>
          <div className="aat-stat-row">
            <div className="aat-stat">
              <span className="aat-stat-value">{stats.bookingSubmitted}</span>
              <span className="aat-stat-label">Submitted</span>
            </div>
            <div className="aat-stat">
              <span className="aat-stat-value" style={{ color: COLORS.success }}>{stats.bookingConfirmed}</span>
              <span className="aat-stat-label">Confirmed</span>
            </div>
            <div className="aat-stat">
              <span className="aat-stat-value" style={{ color: COLORS.danger }}>{stats.bookingDeclined}</span>
              <span className="aat-stat-label">Declined</span>
            </div>
            <div className="aat-stat">
              <span className="aat-stat-value" style={{ color: COLORS.danger }}>{stats.bookingCancelled}</span>
              <span className="aat-stat-label">Cancelled</span>
            </div>
          </div>
        </div>

        <div className="aat-card">
          <div className="aat-card-title">Action Breakdown</div>
          {breakdownRows.length === 0 ? (
            <p className="aat-empty-note">No activity in this range yet.</p>
          ) : (
            <div className="aat-breakdown">
              {breakdownRows.slice(0, 6).map(row => (
                <div className="aat-breakdown-row" key={row.type}>
                  <span className="aat-breakdown-label">{row.label}</span>
                  <div className="aat-breakdown-bar-track">
                    <div
                      className="aat-breakdown-bar-fill"
                      style={{ width: `${row.pct}%`, background: TONE_COLORS[ACTION_TONE[row.type]] || COLORS.cyan }}
                    />
                  </div>
                  <span className="aat-breakdown-count">{row.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ---- Filters ---- */}
      <div className="aat-filters">
        <div className="aat-search">
          <Search size={15} color={COLORS.inkFaint} />
          <input
            type="text"
            placeholder="Search by guest name, booking id, confirmation id or admin..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <select className="aat-select" value={actionFilter} onChange={(e) => setActionFilter(e.target.value)}>
          {ACTION_FILTER_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
        </select>

        <select className="aat-select" value={entityFilter} onChange={(e) => setEntityFilter(e.target.value)}>
          {ENTITY_FILTER_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
        </select>

        <div className="aat-date-filter">
          <label>From</label>
          <input type="date" value={dateFilter.from} onChange={(e) => setDateFilter(prev => ({ ...prev, from: e.target.value }))} />
          <label>To</label>
          <input type="date" value={dateFilter.to} onChange={(e) => setDateFilter(prev => ({ ...prev, to: e.target.value }))} min={dateFilter.from || undefined} />
          {isRanged && (
            <button type="button" className="aat-clear-range" onClick={clearDateFilter} title="Clear date range">
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* The live-listener count is shown in BOTH modes on purpose: it is the
          number of entries the log actually contains in the recent window, so
          if the table ever shows fewer than this without a filter explaining
          the gap, the discrepancy is visible instead of silent. */}
      <p className="aat-live-note">
        {isRanged
          ? `${filteredEntries.length} shown · ${entries.length} logged in this date range (live). Widen the dates to look further back.`
          : 'Pick a date range to load audit activity.'}
      </p>

      {/* ---- Table ---- */}
      {loadError ? (
        <div className="aat-error">
          <strong>Could not read the audit log.</strong>
          <p>{loadError}</p>
          <p className="aat-error-hint">
            A “permission denied” here means <code>/auditLog</code> has no rule in
            database.rules.json yet; “Index not defined” means it needs
            <code> ".indexOn": ["entityId", "timestamp"]</code>. Entries are still being
            written either way — this screen just can’t read them back.
          </p>
        </div>
      ) : loading ? (
        <p className="placeholder-text">Loading…</p>
      ) : filteredEntries.length === 0 ? (
        <p className="placeholder-text">No audit activity found for these filters.</p>
      ) : (
        <div className="aat-table-wrapper">
          <table className="aat-table">
            <thead>
              <tr>
                <th>Date &amp; Time</th>
                <th>Performed By</th>
                <th>Action</th>
                <th>Confirmation ID</th>
                <th>Details</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {pageEntries.map((entry) => {
                const tone = TONE_COLORS[ACTION_TONE[entry.actionType]] || COLORS.ink;
                const isStatusChange = STATUS_CHANGE_ACTIONS.has(entry.actionType);
                const booking = bookingCache[entry.entityId] || null;
                const actorLabel = resolveActorLabel(entry, booking);
                const sessionId = entry.entityType === ENTITY_TYPES.OPENPLAY_SESSION
                  ? entry.entityId
                  : entry.summary?.sessionId;
                const session = sessionId ? (sessionCache[sessionId] || null) : null;
                return (
                  <tr key={entry.id}>
                    <td className="aat-td-date">{formatEntryDate(entry.timestamp)}</td>
                    <td>
                      <span className="aat-actor-badge">{actorLabel}</span>
                    </td>
                    <td>
                      <span className="aat-action-pill" style={{ color: tone, borderColor: tone, background: TONE_BG[ACTION_TONE[entry.actionType]] || COLORS.cyanSoft }}>
                        {AUDIT_ACTION_LABELS[entry.actionType] || entry.actionType}
                      </span>
                      {/* Set by markPastDateEdit — the change was made on a
                          date that had already passed. */}
                      {entry.summary?.pastDateEdit && (
                        <div className="aat-past-tag">Past date edit</div>
                      )}
                    </td>
                    <td>
                      <div className="aat-entity-cell">
                        {/* The confirmation id is the reference an admin can
                            actually act on — it's what the guest was given,
                            what Track Booking searches, and what the bookings
                            list shows. The raw Firebase key means nothing to
                            anyone, so it moves to the tooltip and is only the
                            visible value when an older entry has no
                            confirmation id stored. */}
                        <span
                          className="aat-entity-id"
                          title={`Booking record: ${entry.entityId}`}
                        >
                          {entry.summary?.confirmationId || booking?.confirmationId || entry.entityId}
                        </span>
                      </div>
                    </td>
                    <td className="aat-td-details">
                      <AuditDetails
                        entry={entry}
                        config={config}
                        booking={booking}
                        session={session}
                        actorLabel={actorLabel}
                      />
                    </td>
                    <td>
                      {isStatusChange ? (
                        <span className="aat-status-transition">
                          {formatStatus(entry.statusOld)} <ArrowRight size={12} /> <strong>{formatStatus(entry.statusNew)}</strong>
                        </span>
                      ) : (
                        <span style={{ color: COLORS.inkFaint }}>—</span>
                      )}
                      {entry.reason && (
                        <div className="aat-reason">“{entry.reason}”</div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="aat-pagination">
          <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
            <ChevronLeft size={14} /> Prev
          </button>
          <span>Page {currentPage} of {totalPages}</span>
          <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
            Next <ChevronRight size={14} />
          </button>
        </div>
      )}
    </div>
  );
}

export default AdminAuditTrail;
